<template>
  <!-- ticket #1755: when a vehicle is set OOS, the user gives an estimated time
       of return. that ETR becomes the end of the vehicle's unavailability block
       on the tenant's TBN availability calendar, so dispatch can't book a bus
       the shop has taken down. -->
  <b-modal
    :visible="visible"
    :title="'Out of service - ' + vehicleName"
    ok-title="Set out of service"
    :ok-disabled="!isValid"
    cancel-title="Cancel"
    size="sm"
    @ok="onConfirm"
    @hide="$emit('cancel')"
  >
    <b-form-group label="Estimated return" label-for="oos-etr-date" class="oos-etr-group">
      <div class="oos-etr-row">
        <b-form-input
          id="oos-etr-date"
          v-model="date"
          type="date"
          size="sm"
          :min="todayIso"
          class="oos-etr-date"
          @keydown.enter="onEnter"
        />
        <b-form-input
          v-model="time"
          type="time"
          size="sm"
          class="oos-etr-time"
          :title="timezone ? 'Time at ' + timezone : 'Local time'"
          :aria-label="timezone ? 'Estimated return time, ' + timezone : 'Estimated return time'"
          @keydown.enter="onEnter"
        />
        <span v-if="timezone" class="oos-etr-tz">{{ tzLabel }}</span>
      </div>
      <b-form-invalid-feedback :state="isValid || !isDirty">{{ invalidReason }}</b-form-invalid-feedback>
    </b-form-group>
  </b-modal>
</template>

<script>
import moment from 'moment-timezone'

export default {
  name: 'VsmOosEtrModal',
  props: {
    visible: { type: Boolean, default: false },
    vehicleName: { type: String, default: '' },
    // existing ETR to edit, if the vehicle is already OOS
    value: { type: String, default: null },
    // the tenant's timezone; the value we emit is naive and the API reads it as
    // yard-local, so the label keeps that unambiguous for a user in another zone
    timezone: { type: String, default: '' },
  },
  data() {
    return { date: '', time: '', isDirty: false }
  },
  computed: {
    todayIso() {
      return this.nowInTenantZone().format('YYYY-MM-DD')
    },
    tzLabel() {
      if (!this.timezone) return ''
      return this.nowInTenantZone().format('z')
    },
    combined() {
      if (!this.date || !this.time) return null
      return this.date + 'T' + (this.time.length === 5 ? this.time + ':00' : this.time)
    },
    isValid() {
      if (!this.combined) return false
      const picked = moment(this.combined, 'YYYY-MM-DDTHH:mm:ss', true)
      if (!picked.isValid()) return false
      return picked.isAfter(this.nowInTenantZone().format('YYYY-MM-DDTHH:mm:ss'))
    },
    invalidReason() {
      if (!this.date || !this.time) return 'Pick a date and a time.'
      return 'The estimated return has to be in the future.'
    },
  },
  watch: {
    visible(open) {
      if (open) this.reset()
    },
  },
  methods: {
    // the tenant's wall clock, which is what the backend localizes the naive
    // value to -- not the browser's, which may be a different zone entirely
    nowInTenantZone() {
      return this.timezone ? moment().tz(this.timezone) : moment()
    },
    reset() {
      this.isDirty = false
      const existing = this.value ? moment(this.value) : null
      const seed = existing && existing.isValid()
        ? (this.timezone ? existing.tz(this.timezone) : existing)
        : this.nowInTenantZone().add(4, 'hours').startOf('hour')
      this.date = seed.format('YYYY-MM-DD')
      this.time = seed.format('HH:mm')
    },
    onEnter() {
      if (this.isValid) this.onConfirm()
    },
    onConfirm(evt) {
      this.isDirty = true
      if (!this.isValid) {
        if (evt && evt.preventDefault) evt.preventDefault()
        return
      }
      this.$emit('confirm', this.combined)
    },
  },
}
</script>

<style scoped>
.oos-etr-row { display: flex; align-items: center; gap: 6px; }
.oos-etr-date { flex: 1 1 58%; min-width: 0; }
.oos-etr-time { flex: 1 1 42%; min-width: 0; }
.oos-etr-tz { font-size: 11px; color: #6c757d; white-space: nowrap; }
.oos-etr-group { margin-bottom: 0; }
</style>
